import type { ReactNode } from 'react';
import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';
import { Tooltip } from '../tooltip/tooltip';
import InputLabel from './input-label';

const ICON_SCALE = IconScale.XS;

interface IValidationError {
  hasError: boolean;
  errorMessage: string;
}

interface IInputLabelWithHint {
  showLabel: boolean;
  hintText?: string | ReactNode;
  hasError?: boolean;
  name: string;
  inputId: string;
  validationError?: IValidationError;
}

const InputLabelWithHint = ({
  inputId,
  showLabel,
  hintText,
  hasError,
  name,
  validationError,
}: IInputLabelWithHint) => {
  const theme = useTheme();
  if (!showLabel) return;
  return (
    <LabelWrapper>
      <InputLabel
        theme={theme}
        htmlFor={inputId}
        $hasError={hasError || Boolean(validationError?.hasError)}
      >
        {name}
      </InputLabel>
      {hintText && (
        <Tooltip
          tooltipText={hintText}
          id={name}
          tooltipAnchorStyle={{
            height: `${ICON_SCALE}px`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <StyledIcon
            name={IconName.Info}
            color={theme.palette.neutral[80]}
            scale={ICON_SCALE}
          />
        </Tooltip>
      )}
    </LabelWrapper>
  );
};

export default memo(InputLabelWithHint);

export const LabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(1)};
  `}
`;

const StyledIcon = styled(Icon)`
  line-height: ${ICON_SCALE}px;
  height: ${ICON_SCALE}px;
  display: inline-block;
`;
